import React, { useState, useEffect, useCallback } from "react";
import {
  Table, Button, Tag, Avatar, Tabs, Spin, Empty,
  Row, Col, Statistic, Card, Breadcrumb, message, Space, Tooltip,
} from "antd";
import {
  ArrowLeftOutlined, BankOutlined, MailOutlined, CheckCircleOutlined,
  FileTextOutlined, DollarOutlined, ClockCircleOutlined, SendOutlined,
  EyeOutlined, TeamOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { API_CONST } from "../../../const";

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const OrgCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid #e8e8e8;
  .ant-card-body { padding: 24px; }
`;

const StatsRow = styled(Row)`
  margin-top: 20px;
  border-top: 1px solid #f0f0f0;
  padding-top: 20px;
  .ant-statistic-title { font-size: 12px; color: #888; }
  .ant-statistic-content-value { font-size: 22px; color: #014c75; font-weight: 700; }
`;

const DetailWrapper = styled.div`
  .section-tabs .ant-tabs-nav { margin-bottom: 0; }
`;

const STATUS_COLOR = {
  open: "green",
  draft: "orange",
  closed: "red",
  filled: "blue",
  active: "green",
  terminated: "red",
  completed: "blue",
};

const JOB_STATUS_LABEL = {
  open: "Open",
  draft: "Draft",
  closed: "Closed",
  filled: "Filled",
};

const PartnerClientDetail = () => {
  const { relationId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const headers = { Authorization: `Bearer ${token}` };

  const [relation, setRelation] = useState(null);
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("jobs");

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(
        `${API_CONST.PARTNER_RELATION_CLIENT_STATS}/${relationId}`,
        { headers }
      );
      setRelation(res.data.relation || null);
      setStats(res.data.stats || {});
    } catch {
      message.error("Failed to load client details");
    } finally {
      setStatsLoading(false);
    }
  }, [relationId]);

  const fetchJobs = useCallback(async () => {
    if (!relation?.customerOrganization) return;
    setJobsLoading(true);
    try {
      const res = await fetch(API_CONST.GET_JOB_REQUIREMENTS, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ customerOrganizations: [relation.customerOrganization], pageSize: 100 }),
      });
      const data = await res.json();
      setJobs(data.Response || []);
    } catch {
      message.error("Failed to load jobs");
    } finally {
      setJobsLoading(false);
    }
  }, [relation?.customerOrganization]);

  const fetchContracts = useCallback(async () => {
    if (!relation?.customerOrganization) return;
    setContractsLoading(true);
    try {
      const res = await axios.post(
        API_CONST.GET_HIRING_RECORDS,
        { customerOrganizations: [relation.customerOrganization] },
        { headers }
      );
      setContracts((res.data.hiringRecords || []).filter((r) => r.status !== "draft"));
    } catch {
      message.error("Failed to load contracts");
    } finally {
      setContractsLoading(false);
    }
  }, [relation?.customerOrganization]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab === "jobs" && relation?.customerOrganization) fetchJobs(); }, [activeTab, relation]);
  useEffect(() => { if (activeTab === "contracts" && relation?.customerOrganization) fetchContracts(); }, [activeTab, relation]);

  const jobColumns = [
    {
      title: "Job ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      render: (v) => <Tag style={{ fontFamily: "monospace", fontSize: 11 }}>{v}</Tag>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (v, rec) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 600, color: "#014c75", height: "auto" }}
          onClick={() => navigate(`/partner/submit-profiles/${rec.id}`, {
            state: { backPath: `/partner/my-clients/${relationId}`, backLabel: 'Back to My Clients' },
          })}
        >
          {v}
        </Button>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag color={STATUS_COLOR[v] || "default"}>{JOB_STATUS_LABEL[v] || v}</Tag>,
    },
    {
      title: "Posted",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—",
    },
    {
      title: "Applications",
      dataIndex: "applicationsCount",
      key: "applicationsCount",
      render: (v) => (
        <Tag color="blue">{v ?? 0}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, rec) => (
        <Space>
          <Tooltip title="Submit Profiles">
            <Button
              size="small"
              icon={<SendOutlined />}
              type="primary"
              style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
              onClick={() => navigate(`/partner/submit-profiles/${rec.id}`)}
            />
          </Tooltip>
          <Tooltip title="View Job Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/partner/job-full-details/${rec.id}`, {
                state: { partnerView: true, readOnly: true, backPath: `/partner/my-clients/${relationId}` },
              })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const contractColumns = [
    {
      title: "Contract ID",
      dataIndex: "id",
      key: "id",
      width: 180,
      render: (v) => <span style={{ fontSize: 11, fontFamily: "monospace" }}>{v}</span>,
    },
    {
      title: "Talent",
      dataIndex: "talentName",
      key: "talentName",
      render: (v) => <strong>{v || "—"}</strong>,
    },
    { title: "Role", dataIndex: "talentRole", key: "talentRole" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag color={STATUS_COLOR[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—",
    },
    {
      title: "Partner Rate",
      dataIndex: "partnerRate",
      key: "partnerRate",
      render: (v, rec) => v ? `${rec.currency || "INR"} ${Number(v).toLocaleString()}/mo` : "—",
    },
  ];

  const clientName = relation?.customerOrganization || "Client";

  return (
    <DetailWrapper>
      <PageHeader>
        <Breadcrumb
          items={[
            {
              title: (
                <span onClick={() => navigate("/partner/my-clients")} style={{ cursor: "pointer", color: "#014c75" }}>
                  My Clients
                </span>
              ),
            },
            { title: clientName },
          ]}
          style={{ marginBottom: 12 }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/partner/my-clients")}>Back</Button>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#014c75" }}>{clientName}</h1>
        </div>
      </PageHeader>

      <Spin spinning={statsLoading}>
        <OrgCard>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Avatar size={54} icon={<BankOutlined />} style={{ background: "#e6f7ff", color: "#014c75" }} />
            <div>
              <h2 style={{ margin: 0, color: "#014c75", fontSize: 18 }}>{clientName}</h2>
              {relation?.invitedEmail && (
                <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
                  <MailOutlined style={{ marginRight: 6 }} />{relation.invitedEmail}
                </div>
              )}
              {relation?.acceptedAt && (
                <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
                  <CheckCircleOutlined style={{ marginRight: 6 }} />
                  Client since {new Date(relation.acceptedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              )}
            </div>
            <div style={{ marginLeft: "auto" }}>
              <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: "4px 10px" }}>
                Active
              </Tag>
            </div>
          </div>

          <StatsRow gutter={[0, 0]}>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Jobs Posted" value={stats.jobsPosted ?? 0} prefix={<FileTextOutlined />} />
            </Col>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Submitted" value={stats.applicationsSubmitted ?? 0} />
            </Col>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Hired" value={stats.hiredByClient ?? 0} valueStyle={{ color: "#014c75" }} />
            </Col>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Active Hires" value={stats.activeHiresByClient ?? 0} valueStyle={{ color: "#52c41a" }} />
            </Col>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Rejected" value={stats.applicationsRejected ?? 0} valueStyle={{ color: "#ff4d4f" }} />
            </Col>
            <Col span={4} style={{ textAlign: "center" }}>
              <Statistic title="My Bench" value={stats.benchCount ?? 0} valueStyle={{ color: "#fa8c16" }} prefix={<TeamOutlined />} />
            </Col>
          </StatsRow>
        </OrgCard>
      </Spin>

      <Tabs
        className="section-tabs"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "jobs",
            label: <span><FileTextOutlined /> Jobs</span>,
            children: (
              <Card style={{ borderRadius: 8 }}>
                <Table
                  dataSource={jobs}
                  columns={jobColumns}
                  rowKey="id"
                  loading={jobsLoading}
                  pagination={{ pageSize: 10, showTotal: (t) => `${t} jobs`, showSizeChanger: true }}
                  locale={{ emptyText: <Empty description="No jobs from this client" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                  size="middle"
                />
              </Card>
            ),
          },
          {
            key: "contracts",
            label: <span><DollarOutlined /> Contracts</span>,
            children: (
              <Card style={{ borderRadius: 8 }}>
                {contracts.length > 0 && (
                  <Row gutter={16} style={{ marginBottom: 20 }}>
                    <Col span={6}>
                      <Card size="small" style={{ textAlign: "center", background: "#f6ffed", borderColor: "#b7eb8f" }}>
                        <Statistic title="Active" value={contracts.filter((c) => c.status === "active").length} valueStyle={{ color: "#52c41a", fontSize: 24 }} prefix={<CheckCircleOutlined />} />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ textAlign: "center", background: "#e6f7ff", borderColor: "#91d5ff" }}>
                        <Statistic title="Total Contracts" value={contracts.length} valueStyle={{ color: "#1890ff", fontSize: 24 }} prefix={<FileTextOutlined />} />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ textAlign: "center", background: "#fff7e6", borderColor: "#ffd591" }}>
                        <Statistic
                          title="Active Partner Revenue"
                          value={contracts.filter((c) => c.status === "active").reduce((s, c) => s + (Number(c.settledPartnerRate) || Number(c.partnerRate) || 0), 0).toLocaleString()}
                          prefix={<DollarOutlined />}
                          suffix="/mo"
                          valueStyle={{ color: "#fa8c16", fontSize: 18 }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ textAlign: "center", background: "#f9f0ff", borderColor: "#d3adf7" }}>
                        <Statistic title="Completed" value={contracts.filter((c) => c.status === "completed").length} valueStyle={{ color: "#722ed1", fontSize: 24 }} prefix={<ClockCircleOutlined />} />
                      </Card>
                    </Col>
                  </Row>
                )}

                <Table
                  dataSource={contracts}
                  columns={contractColumns}
                  rowKey="id"
                  loading={contractsLoading}
                  pagination={{ pageSize: 10, showTotal: (t) => `${t} contracts` }}
                  locale={{ emptyText: <Empty description="No contracts yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                  size="middle"
                />
              </Card>
            ),
          },
        ]}
      />
    </DetailWrapper>
  );
};

export default PartnerClientDetail;
